import React from 'react';
import { t } from '../utils/translations';

const MessageFormatter = ({ content, userLanguage = 'en' }) => {
  // Function to detect code blocks
  const formatCodeBlocks = (text) => {
    // Match code blocks with ```language or just ```
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }

      // Add code block
      parts.push({
        type: 'code',
        language: match[1] || 'text',
        content: match[2].trim()
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Function to format inline code
  const formatInlineCode = (text) => {
    return text.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
  };

  // Function to highlight Python syntax
  const highlightPython = (code) => {
    // Escape HTML first
    let highlighted = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Process strings FIRST to avoid highlighting # inside strings as comments
    // Triple-quoted strings (docstrings) - process these first
    highlighted = highlighted.replace(/(['"]{3})([\s\S]*?)\1/g, (match, quotes, content) => {
      return quotes + '<span class="string">' + content + '</span>' + quotes;
    });
    
    // Regular strings (single and double quotes)
    highlighted = highlighted.replace(/(['"])((?:(?!\1)[^\\]|\\.)*?)\1/g, '<span class="string">$1$2$1</span>');
    
    // Now process comments line by line, skipping those inside string spans
    const lines = highlighted.split('\n');
    highlighted = lines.map(line => {
      // Find # in the line
      const hashIndex = line.indexOf('#');
      if (hashIndex === -1) {
        return line;
      }
      
      // Check if this # is inside a string span
      const beforeHash = line.substring(0, hashIndex);
      const afterHash = line.substring(hashIndex);
      
      // Count string spans before the hash
      const openSpans = (beforeHash.match(/<span class="string">/g) || []).length;
      const closeSpans = (beforeHash.match(/<\/span>/g) || []).length;
      const isInString = openSpans > closeSpans;
      
      if (!isInString) {
        return beforeHash + '<span class="comment">' + afterHash + '</span>';
      }
      
      return line;
    }).join('\n');
    
    // Numbers (avoid matching inside strings by checking context)
    highlighted = highlighted.replace(/\b(\d+\.?\d*)\b/g, '<span class="number">$1</span>');
    
    // Decorators
    highlighted = highlighted.replace(/@(\w+)/g, '<span class="decorator">@$1</span>');
    
    // Function definitions (def keyword + function name)
    highlighted = highlighted.replace(/\bdef\s+(\w+)\s*\(/g, '<span class="keyword">def</span> <span class="function">$1</span>(');
    
    // Class definitions
    highlighted = highlighted.replace(/\bclass\s+(\w+)/g, '<span class="keyword">class</span> <span class="class">$1</span>');
    
    // Built-in functions and constants
    const builtins = ['print', 'len', 'range', 'str', 'int', 'float', 'list', 'dict', 'tuple', 'set', 'bool', 'type', 'isinstance', 'hasattr', 'getattr', 'setattr', 'delattr', 'super', 'self', 'None', 'True', 'False'];
    builtins.forEach(builtin => {
      const regex = new RegExp(`\\b(${builtin})\\b(?!</span>)`, 'g');
      highlighted = highlighted.replace(regex, '<span class="builtin">$1</span>');
    });
    
    // Python keywords (excluding def and class which are already handled)
    const keywords = ['if', 'elif', 'else', 'for', 'while', 'try', 'except', 'finally', 'with', 'as', 'import', 'from', 'return', 'yield', 'break', 'continue', 'pass', 'raise', 'assert', 'lambda', 'and', 'or', 'not', 'in', 'is', 'async', 'await', 'global', 'nonlocal', 'del'];
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b(${keyword})\\b(?!</span>)`, 'g');
      highlighted = highlighted.replace(regex, '<span class="keyword">$1</span>');
    });
    
    return highlighted;
  };

  // Function to format markdown headers
  const formatHeaders = (text) => {
    const lines = text.split('\n');
    const formattedLines = lines.map(line => {
      // Match markdown headers: # for h1, ## for h2, ### for h3, etc. (up to 6 levels)
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length; // Number of # characters
        const headerText = headerMatch[2].trim();
        return `<h${level} class="markdown-header markdown-h${level}">${headerText}</h${level}>`;
      }
      return line;
    });
    return formattedLines.join('\n');
  };

  // Function to format lists
  const formatLists = (text) => {
    const lines = text.split('\n');
    const formattedLines = [];
    let inList = false;
    let listItems = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Check if line starts with list markers (but not if it's already a header)
      if (!trimmedLine.match(/^<h[1-6]/) && (trimmedLine.match(/^[-*+]\s/) || trimmedLine.match(/^\d+\.\s/))) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(trimmedLine);
      } else {
        if (inList) {
          // Process accumulated list items
          formattedLines.push(renderList(listItems));
          listItems = [];
          inList = false;
        }
        formattedLines.push(line);
      }
    }

    // Handle case where text ends with a list
    if (inList && listItems.length > 0) {
      formattedLines.push(renderList(listItems));
    }

    return formattedLines.join('\n');
  };

  // Function to render list items
  const renderList = (items) => {
    const isOrdered = items[0].match(/^\d+\.\s/);
    const listType = isOrdered ? 'ol' : 'ul';
    const listClass = isOrdered ? 'ordered-list' : 'unordered-list';

    const listItems = items.map((item, index) => {
      const content = item.replace(/^[-*+\d.]\s/, '');
      return `<li class="list-item">${content}</li>`;
    }).join('');

    return `<${listType} class="${listClass}">${listItems}</${listType}>`;
  };

  // Function to format bold and italic text
  const formatText = (text) => {
    // Bold text **text** or __text__
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Italic text *text* or _text_
    text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
    text = text.replace(/_(.*?)_/g, '<em>$1</em>');

    return text;
  };

  // Function to format links
  const formatLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return text.replace(linkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  };

  // Main formatting function
  const formatContent = (content) => {
    // First, handle code blocks
    const codeParts = formatCodeBlocks(content);

    return codeParts.map((part, index) => {
      if (part.type === 'code') {
        // Apply Python syntax highlighting if language is Python
        const isPython = part.language && (part.language.toLowerCase() === 'python' || part.language.toLowerCase() === 'py');
        const codeContent = isPython ? highlightPython(part.content) : part.content;
        const containerClass = isPython 
          ? 'code-block-container code-block-python' 
          : 'code-block-container';
        
        return (
          <div key={index} className={containerClass}>
            <div className="code-block-header">
              <span className="code-language">{part.language}</span>
              <button
                className="copy-button"
                onClick={() => navigator.clipboard.writeText(part.content)}
                title={t('copyCode', userLanguage)}
              >
                📋
              </button>
            </div>
            <pre className="code-block">
              <code 
                className={`language-${part.language}`}
                dangerouslySetInnerHTML={{ __html: codeContent }}
              />
            </pre>
          </div>
        );
      } else {
        // Format text content
        let formattedText = part.content;
        formattedText = formatHeaders(formattedText);
        formattedText = formatLists(formattedText);
        formattedText = formatInlineCode(formattedText);
        formattedText = formatText(formattedText);
        formattedText = formatLinks(formattedText);

        return (
          <div
            key={index}
            className="formatted-text"
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        );
      }
    });
  };

  return (
    <div className="message-formatter">
      {formatContent(content)}
    </div>
  );
};

export default MessageFormatter;